import { CommonButton, ModalForm, Select, TControlledFormComponentProps, TControlledFormProps } from '@chocolate-soup-inc/cs-frontend-components';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { AWSDateSchema } from '../../../entities/shared/dateTime';
import { TPrintShipmentLabelMutationInput, TProduct, usePrintShipmentLabelMutation } from '../../../generated/graphql';

import clsx from 'clsx';
import { TGiftPackType } from '../../../entities/giftPackages/queries';
import { getProductIdentifierFormFields } from '../../../entities/products/formFields';
import { useQueryAllProducts } from '../../../entities/products/queries';
import { getProductDefaultValues, productIdentifierFields } from '../../../entities/products/schema';
import { pickProductsMapDefaultValues } from '../../../entities/products/shared';
import { TShipmentType } from '../../../entities/shipments/queries';
import { useShipmentAddressInfo } from '../../../entities/shipments/shared';
import { getDefaultCustoms } from './customs';
import styles from './PrintLabelModal.module.scss';

export type TPrintLabelModalProps = {
  shipment?: TShipmentType;
  onCancel: () => void;
  onSuccess: () => void;
  defaultPackages: TGiftPackType[];
  fromMultiple?: boolean;
  setAllMultiple?: boolean;
  setShipmentsToUpdate?: React.Dispatch<React.SetStateAction<TShipmentType[]>> | null;
};

export const PrintLabelModal = (props: TPrintLabelModalProps) => {
  const { shipment, onCancel, onSuccess, defaultPackages, fromMultiple, setAllMultiple, setShipmentsToUpdate } = props;
  const { recipientName } = useShipmentAddressInfo(shipment);

  const isInternational = useMemo(() => shipment?.address?.country != 'CAN', [shipment]);
  //console.log('shipment.customData', shipment.customsData);

  const defaultCustoms = useMemo(() => {
    if (!shipment) return null;
    if (!isInternational) return null;
    if (!shipment.customsData) return getDefaultCustoms({ shipment, defaultPackages });
    return pickProductsMapDefaultValues(shipment.customsData as TProduct[]);
  }, [shipment, isInternational, defaultPackages]);

  const defaultValues = useMemo(() => {
    return {
      customsData: defaultCustoms ?? []
    };
  }, [defaultCustoms]);

  const formFields: TControlledFormComponentProps[] = useMemo(() => {
    const fields: TControlledFormComponentProps[] = [];

    if (!(fromMultiple || setAllMultiple))
      fields.push({
        type: 'datePicker',
        label: 'Shipping Date',
        name: 'actualShippingDate',
        disabled: fromMultiple
      });

    if (isInternational)
      fields.push({
        type: 'fieldArray',
        name: 'customsData',
        addLabel: 'Add Customs Item',
        fields: [...getProductIdentifierFormFields()],
        label: 'Customs Item',
        removeLabel: 'Remove Customs Item'
      });

    return fields;
  }, [isInternational, fromMultiple, setAllMultiple]);

  const schema = useMemo(() => {
    return Joi.object()
      .unknown(false)
      .keys({
        actualShippingDate: fromMultiple || setAllMultiple ? AWSDateSchema.optional() : AWSDateSchema.required(),
        customsData: isInternational
          ? Joi.array()
              .items(
                Joi.object()
                  .unknown(false)
                  .keys({
                    ...productIdentifierFields
                  })
                  .and('manufacturerProductId', 'manufacturerProductIdType', { isPresent: (value) => value != null })
              )
              .required()
              .min(1)
          : Joi.optional().allow(null)
      });
  }, [fromMultiple, setAllMultiple, isInternational]);

  const headline = useMemo(() => {
    if (setAllMultiple) return `Customs data for ALL shipments`;
    if (fromMultiple) return `Customs data for ${shipment?.recipientName ?? recipientName} at ${shipment?.company?.name ?? '-'}`;
    return `Print Label for ${shipment?.recipientName ?? recipientName} at ${shipment?.company?.name ?? '-'}`;
  }, [recipientName, shipment, setAllMultiple, fromMultiple]);

  const supportingText = useMemo(() => {
    return `Set the ${setAllMultiple ? '' : 'actual shipping date'} ${!setAllMultiple && isInternational ? 'and' : ''} ${isInternational ? 'customs data' : ''} for ${setAllMultiple ? 'all' : 'this'} shipment${setAllMultiple ? 's' : ''}?`;
  }, [isInternational, setAllMultiple]);

  const confirmLabel = useMemo(() => {
    if (fromMultiple || setAllMultiple) return `Update Customs`;
    return `Print Label`;
  }, [fromMultiple, setAllMultiple]);

  const [printLabel] = usePrintShipmentLabelMutation();

  const onSubmit = useCallback<TControlledFormProps<TPrintShipmentLabelMutationInput>['onValidSubmit']>(
    (data) => {
      const customsData =
        data.customsData?.map((item) =>
          item
            ? {
                ...item,
                originCriterion: item.originCriterion ?? null,
                sku: item.sku ?? null,
                manufacturerNsProductId: item.manufacturerNsProductId ?? null,
                manufacturerProductId: item.manufacturerProductId ?? null,
                manufacturerProductIdType: item.manufacturerProductIdType ?? null
              }
            : null
        ) ?? null;

      if (setAllMultiple) {
        try {
          if (setShipmentsToUpdate) setShipmentsToUpdate((prev) => prev.map((s) => ({ ...s, customsData })));

          onSuccess();
        } catch (error) {
          console.warn('onSubmit()->error:', error);
          toast.error('There was an error updating the shipment.');
        }
        return;
      }

      if (!shipment) return;

      if (fromMultiple) {
        try {
          if (setShipmentsToUpdate)
            setShipmentsToUpdate((prev) => {
              const index = prev.findIndex((s) => s.id === shipment.id);
              if (index === -1) return prev;
              prev[index].customsData = customsData;
              return prev;
            });

          onSuccess();
        } catch (error) {
          console.warn('onSubmit()->error:', error);
          toast.error('There was an error updating the shipment.');
        }
        return;
      }

      return printLabel({
        variables: {
          id: shipment.id,
          companyId: shipment.companyId,
          version: shipment._version,
          input: { ...data, customsData }
        }
      })
        .then(() => {
          if (onSuccess) onSuccess();
        })
        .catch((error) => {
          console.error(serializeError(error));
          toast.error('There was an error printing the shipment labels.');
        });
    },
    [onSuccess, printLabel, shipment, fromMultiple, setAllMultiple, setShipmentsToUpdate]
  );

  const { data: products, loading: loadingProducts, error: errorProducts } = useQueryAllProducts();

  const formRef = useRef<any>(null);

  const [existingProductSeleted, setExistingProductSeleted] = useState<string | null>();

  const productsOpts = useMemo(() => {
    return products.map((p) => ({
      label: `${p.description} [${p.quantity}u, $${p.value} CAD, ${p.hsCode}]`,
      value: p.id
    }));
  }, [products]);

  const onProductSelected = useCallback(() => {
    const prev = formRef.current?.getValues('customsData') || [];
    const product = getProductDefaultValues(products.find((p) => p.id == existingProductSeleted));
    formRef.current?.setNewValue('customsData', [...prev, product]);
    setExistingProductSeleted(null);
  }, [existingProductSeleted, products]);

  return (
    <ModalForm
      closeModal={onCancel}
      controlledFormRef={formRef}
      confirmLabel={confirmLabel}
      contentClassName={styles.setAsReadyToShipModal}
      controlledFormProps={{
        className: styles.form,
        fields: formFields as TControlledFormComponentProps<TPrintShipmentLabelMutationInput>[],
        formProps: {
          defaultValues,
          resolver: joiResolver(schema, {
            convert: true,
            abortEarly: false,
            stripUnknown: false
          })
        },
        onValidSubmit: onSubmit,
        onInvalidSubmit: (errors) => {
          console.error(errors);
          for (const { message } of Object.values(errors)) if (message) toast.error(message);
        }
      }}
      headline={headline}
      onCancelClick={onCancel}
      size='large'
      supportingText={supportingText}>
      {errorProducts && <div className={clsx(styles.containerAddExisingProduct)}>An error occurred while loading the existing products.</div>}
      {isInternational && (
        <div className={clsx(styles.containerAddExisingProduct)}>
          <h4>Add Existing Product</h4>
          <div className={clsx(styles.containerProduct)}>
            <span>Select Product: </span>
            <div className={clsx(styles.existingProducts)}>
              <Select
                variant='outlined'
                name='existingProduct'
                label='Existing Product'
                multiple={false}
                options={productsOpts}
                onChange={(e) => {
                  setExistingProductSeleted(e as string);
                }}
                value={existingProductSeleted}
                disabled={loadingProducts}
              />
              <CommonButton className={clsx(styles.addButton)} label='Add Product' onClick={onProductSelected} variant='filled' disabled={!existingProductSeleted || loadingProducts} loading={loadingProducts} />
            </div>
          </div>
        </div>
      )}
    </ModalForm>
  );
};
